import { createTheme } from '@mui/material/styles'
import type { Theme, ThemeOptions } from '@mui/material/styles'
import type { EditorLayout } from './types'
import { convertStylesToSx, parseStyleString } from './utils'

// Строим MUI-тему формы из layout: парсим layout.theme (JSON-строка с
// ThemeOptions) и мержим с дефолтами. Вынесено из FormRender, чтобы тему
// можно было применять и вне его ThemeProvider (например, к диалогу
// восстановления сессии в viewer-app).
export function buildFormTheme(layout?: EditorLayout): Theme {
  // Извлекаем базовые настройки шрифта из bodyStyles, чтобы применить их
  // к MUI Typography (paragraph/text рендерятся как body1 и по умолчанию
  // берут fontFamily/fontSize/fontWeight из темы, перекрывая наследование).
  const body = (
    layout?.bodyStyles
      ? convertStylesToSx(parseStyleString(layout.bodyStyles))
      : {}
  ) as Record<string, any>

  const bodyVariant: Record<string, any> = {}
  if (body.fontFamily) bodyVariant.fontFamily = body.fontFamily
  if (body.fontSize) bodyVariant.fontSize = body.fontSize
  if (body.fontWeight) bodyVariant.fontWeight = body.fontWeight
  if (body.color) bodyVariant.color = body.color

  const bodyTypography: Record<string, any> = {
    ...(body.fontFamily ? { fontFamily: body.fontFamily } : {}),
    ...(Object.keys(bodyVariant).length > 0
      ? { body1: { ...bodyVariant }, body2: { ...bodyVariant } }
      : {}),
  }

  const defaultHeadings = {
    h1: {
      fontSize: '28px',
      fontWeight: 600,
      marginBottom: '16px !important',
    },
    h2: {
      fontSize: '22px',
      fontWeight: 600,
      marginBottom: '14px !important',
    },
    h3: {
      fontSize: '17.5px',
      fontWeight: 600,
      marginBottom: '12px !important',
    },
    h4: {
      fontSize: '16px',
      fontWeight: 600,
      marginBottom: '10px !important',
    },
    h5: {
      fontSize: '14px',
      fontWeight: 600,
      marginBottom: '8px !important',
    },
    h6: {
      fontSize: '12px',
      fontWeight: 600,
      marginBottom: '6px !important',
    },
  }

  const defaultThemeOptions: ThemeOptions = {
    typography: {
      ...bodyTypography,
      ...defaultHeadings,
    },
    components: {
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'medium',
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          size: 'medium',
        },
      },
    },
  }

  if (!layout?.theme) {
    return createTheme(defaultThemeOptions)
  }
  try {
    const themeOptions = JSON.parse(layout.theme) as ThemeOptions

    // Мержим с дефолтными настройками, чтобы все headings имели дефолтные значения
    const existingTypography = themeOptions.typography
    const mergedThemeOptions: ThemeOptions = {
      ...themeOptions,
      typography:
        typeof existingTypography === 'object' && existingTypography !== null
          ? {
              ...existingTypography,
              ...bodyTypography,
              body1: {
                ...(existingTypography.body1 as any),
                ...(bodyTypography.body1 as any),
              },
              body2: {
                ...(existingTypography.body2 as any),
                ...(bodyTypography.body2 as any),
              },
              h1: {
                ...(existingTypography.h1 as any),
                ...defaultHeadings.h1,
              },
              h2: {
                ...(existingTypography.h2 as any),
                ...defaultHeadings.h2,
              },
              h3: {
                ...(existingTypography.h3 as any),
                ...defaultHeadings.h3,
              },
              h4: {
                ...(existingTypography.h4 as any),
                ...defaultHeadings.h4,
              },
              h5: {
                ...(existingTypography.h5 as any),
                ...defaultHeadings.h5,
              },
              h6: {
                ...(existingTypography.h6 as any),
                ...defaultHeadings.h6,
              },
            }
          : {
              ...bodyTypography,
              ...defaultHeadings,
            },
    }

    return createTheme(mergedThemeOptions)
  } catch (error) {
    console.error('Error parsing theme:', error, layout.theme)

    return createTheme(defaultThemeOptions)
  }
}
